/**
 * queryClient — the React Query brain, configured once for the whole app.
 *
 * WHAT REACT QUERY DOES (and why we don't hand-roll it)
 * -----------------------------------------------------
 * The naive way to load data is: useState for data, useState for loading,
 * useState for error, a useEffect to fetch, and manual refetch logic — in
 * EVERY component. It is dozens of lines each and everyone gets the edge cases
 * wrong (race conditions, stale data, double fetches).
 *
 * React Query replaces all of that with one hook, and adds for free:
 *   • CACHING — ask for the same data twice and the second is instant
 *   • DEDUPLICATION — ten components asking at once fire ONE request
 *   • BACKGROUND REFRESH — data silently updates so the UI is never stale
 *   • RETRIES — a failed request retries automatically
 *
 * The defaults below are the policy for the entire app.
 */

import { QueryClient } from '@tanstack/react-query';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      /**
       * staleTime — how long fetched data is considered "fresh". While fresh,
       * navigating back to a page shows the cached data INSTANTLY with no
       * refetch. 60s is a good default for a product catalogue that does not
       * change second-to-second.
       */
      staleTime: 60_000,

      /**
       * gcTime — how long unused data stays in the cache before being dropped.
       * 5 minutes means returning to a page within 5 min is instant.
       */
      gcTime: 5 * 60_000,

      /**
       * retry — retry a failed request up to twice before showing an error.
       * Smooths over brief network blips on college WiFi. We do NOT retry
       * 404s, since a missing thing will still be missing on retry.
       */
      retry: (failureCount, error) => {
        if (error?.status === 404) return false;
        return failureCount < 2;
      },

      /**
       * Do not refetch just because the user tabbed away and back. It is
       * surprising during development and rarely what a storefront needs.
       */
      refetchOnWindowFocus: false,
    },

    mutations: {
      // Mutations (create/update/delete) are user-initiated; a silent retry
      // could double-submit. Fail fast and let the user decide.
      retry: false,
    },
  },
});

export default queryClient;
